import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';

import { IItemAnswerCode } from './types';
import { IResearchStudy, IResearchStudyDisplay, IResearchSubject, IWebLocation } from './interfaces';
import { RECRUITMENT_ELIGIBILITY_SCORE_URL, UNKNOWN_ANSWER_CODING } from './constants';
import { RemoveHyphenPipe } from '../lib/pipes/remove-hyphen.pipe';

const removeHyphenPipe = new RemoveHyphenPipe();

export function getBackground(note: fhir4.Annotation[]): string {
  return note?.[0].text || 'N/A';
}

export function getCriteriaAnswers(questionnaireResponseItem: fhir4.QuestionnaireResponseItem[], ids: string[]): fhir4.QuestionnaireResponseItem[] {
  return questionnaireResponseItem?.filter(item => ids.includes(item.linkId));
}

export function getRequiredCriteriaCount(group: fhir4.Group): number {
  return group?.characteristic.filter(obj => obj.valueBoolean && !isInformationalCriteria(obj.extension)).length || 0;
}

export function getUnansweredRequiredCriteriaCount(group: fhir4.Group, questionnaireResponseItem: fhir4.QuestionnaireResponse): number {
  const requiredCriteriaIDs = group?.characteristic.filter(obj => obj.valueBoolean && !isInformationalCriteria(obj.extension)).map(obj => obj.id);

  return questionnaireResponseItem?.item.filter(item => {
    return requiredCriteriaIDs.includes(item.linkId) && (item.answer[0].valueCoding.code as IItemAnswerCode) === 'unknown';
  }).length;
}

export function getQualityScore(extensions: fhir4.Extension[]): fhir4.Ratio | null {
  const qualityScoreData = extensions?.find(extension => extension.url.includes('recruitment-eligibility-score')) || null;

  return qualityScoreData?.valueRatio;
}

export function getResearchStudyDisplay(researchStudy: IResearchStudy): IResearchStudyDisplay {
  return {
    background: getBackground(researchStudy.note),
    description: researchStudy.description || 'N/A',
    patientMaterialURL: getStudyURL(researchStudy.webLocation, 'recruitment-page'),
    phase: getPhase(researchStudy.phase),
    primaryObjective: getPrimaryObjective(researchStudy.objective),
    protocolLinkURL: getStudyURL(researchStudy.webLocation, 'registry-page'),
    sponsorLinkURL: getStudyURL(researchStudy.webLocation, 'contact-page'),
    researchStudy,
    trialSummary: researchStudy.descriptionSummary || 'N/A',
    therapeuticAreas: researchStudy.category?.map(item => item.coding[0].code)
  };
}

export function getCriteriaCountByCriteriaType(questionnaireResponseItem: fhir4.QuestionnaireResponse, criteriaType: IItemAnswerCode): number {
  return questionnaireResponseItem?.item.filter(obj => {
    return (obj.answer[0].valueCoding.code as IItemAnswerCode) === criteriaType;
  }).length || 0;
}

export function getStudyURL(webLocations: IWebLocation[], type: string): string {
  return webLocations?.find(obj => obj.type?.coding[0].code === type)?.url || '';
}

export function getPhase(phase: fhir4.CodeableConcept): string {
  return removeHyphenPipe.transform(phase?.coding[0]?.code) || 'N/A';
}


export function getPrimaryObjective(objectives: fhir4.ResearchStudyObjective[]): string {
  return objectives?.find( objective => objective.type?.coding[0].code === 'primary')?.name || 'N/A';
}

export function initExtensionForm(group: fhir4.Group, extensions?: fhir4.Extension[]): { qualityScoreForm: FormGroup, otherExtensions: fhir4.Extension[] } {
  let qualityScoreExtension: fhir4.Extension = null;
  const otherExtensions: fhir4.Extension[] = [];

  if (extensions?.length) {
    extensions.forEach(obj => {
      if (obj.url.includes('recruitment-eligibility-score')) {
        qualityScoreExtension = obj;
      }
      else {
        otherExtensions.push(obj);
      }
    });
  }

  const qualityScoreForm: FormGroup = new FormGroup({
    url: new FormControl(RECRUITMENT_ELIGIBILITY_SCORE_URL),
    valueRatio: new FormGroup({
      numerator: new FormGroup({
        value: new FormControl(qualityScoreExtension?.valueRatio?.numerator?.value || 0)
      }),
      denominator: new FormGroup({
        value: new FormControl(getRequiredCriteriaCount(group))
      }),
    })
  });

  return {
    qualityScoreForm,
    otherExtensions
  };
}

export function initCriteriaForm(group: fhir4.Group, questionnaireResponse: fhir4.QuestionnaireResponse, researchSubject: IResearchSubject): FormGroup {
  return new FormGroup({
    resourceType: new FormControl('QuestionnaireResponse', [Validators.required]),
    item: initItemFormArray(group, questionnaireResponse),
    status: new FormControl(questionnaireResponse?.status || 'completed', [Validators.required]),
    subject: new FormGroup({
      reference: new FormControl(questionnaireResponse?.subject?.reference || `ResearchSubject/${ researchSubject.id }`, [Validators.required])
    }),
  });
}

export function initItemFormArray(group: fhir4.Group, questionnaireResponse?: fhir4.QuestionnaireResponse): FormArray {
  return new FormArray(group?.characteristic?.map(characteristic => {
    const questionnaireResponseItem: fhir4.QuestionnaireResponseItem = questionnaireResponse?.item?.find(item => item.linkId === characteristic.id);

    return initItemForm(characteristic.id, questionnaireResponseItem);
  }));
}

export function initItemForm(linkId: string, item?: fhir4.QuestionnaireResponseItem): FormGroup {
  return new FormGroup({
    linkId: new FormControl(linkId, [Validators.required]),
    answer: new FormArray(item?.answer?.map(answer => initValueCodingForm(answer.valueCoding)) || [initValueCodingForm(UNKNOWN_ANSWER_CODING)])
  });
}

export function initValueCodingForm(valueCoding: fhir4.Coding): FormGroup {
  return new FormGroup({
    valueCoding: new FormGroup({
      system: new FormControl(valueCoding?.system),
      code: new FormControl(valueCoding.code, [Validators.required]),
      display: new FormControl(valueCoding.display, [Validators.required])
    })
  });
}

export function isInformationalCriteria(extensions: fhir4.Extension[]): boolean {
  return extensions?.find(obj => obj.url.includes('cqf-expression')) === undefined;
}

export function sortByDateFunction(a: string, b: string): number {
  const aAsDate = new Date(a).getTime();
  const bAsDate = new Date(b).getTime();

  return bAsDate - aAsDate;
}
